import { useState, useEffect, useMemo } from "react";
import AccordionSection from "./AccordionSection.jsx";
import { asset } from "../assetUrl.js";
import TextureGrid from "./TextureGrid.jsx";
import FilterBar from "./FilterBar.jsx";
import ProductGrid from "./ProductGrid.jsx";
import {
  countertops,
  compatibleStorages,
  allowsNoStorage,
  resolveCountertop,
  doubleAvailable,
  SIZES,
  SINKS,
  TYPES,
} from "../data/vanityData.js";
import { countertopColors, storageColors } from "../data/vanityColors.js";
import { vanityTotal } from "../data/vanityPricing.js";

const DEFAULT_COUNTERTOP_COLOR = "quasar-white";
const DEFAULT_STORAGE_COLOR = "alpine-white";

// Which accordion is open (single-open behaviour like the kitchen panel).
const SECTIONS = { COUNTERTOP: "countertop", STORAGE: "storage", CT_COLOR: "ctColor", ST_COLOR: "stColor" };

export default function VanityConfigurator({
  onCountertopModel,
  onStorageModel,
  onTextureChange,
  onPriceChange,
  onInfoChange,
}) {
  const [openSection, setOpenSection] = useState(SECTIONS.COUNTERTOP);
  // Defaults on open: 80cm, Single, Classic, Ramp basin - and the model is
  // shown straight away rather than waiting for a grid click.
  const [filters, setFilters] = useState({ size: 80, sinks: "single", thickness: "classic" });
  // Remembered so that changing Size or Type keeps the same basin/orientation.
  const [prefer, setPrefer] = useState({ basin: "ramp", orientation: null });

  const [countertop, setCountertop] = useState(() =>
    resolveCountertop({ size: 80, sinks: "single", thickness: "classic" }, { basin: "ramp" })
  );
  const [storage, setStorage] = useState(null); // storage object | "none" | null (unset)

  const [ctColorId, setCtColorId] = useState(DEFAULT_COUNTERTOP_COLOR);
  const [stColorId, setStColorId] = useState(DEFAULT_STORAGE_COLOR);

  const ctColor = countertopColors.find((c) => c.id === ctColorId);
  const stColor = storageColors.find((c) => c.id === stColorId);

  // Type 3 (floating SOLID SURFACE shelf) is made of the same solid surface as
  // the countertop, so it takes the countertop finish and has no colour of its own.
  const storageIsSolidSurface = storage && storage !== "none" && storage.type === 3;
  const showStorageColor = storage && storage !== "none" && !storageIsSolidSurface;

  // Dedicated artwork per size for the Size filter (public/images/vanity/sizes).
  const sizeImages = useMemo(() => {
    const map = {};
    SIZES.forEach((sz) => {
      map[sz] = asset(`/images/vanity/sizes/${sz}.png`);
    });
    return map;
  }, []);

  // Filtered countertop list.
  const filteredCountertops = useMemo(
    () =>
      countertops.filter(
        (c) =>
          (filters.size == null || c.size === filters.size) &&
          (filters.sinks == null || c.sinks === filters.sinks) &&
          (filters.thickness == null || c.thickness === filters.thickness)
      ),
    [filters]
  );

  // Storages compatible with the chosen countertop.
  const storageOptions = useMemo(
    () => (countertop ? compatibleStorages(countertop) : []),
    [countertop]
  );
  const canHaveNoStorage = allowsNoStorage(countertop);

  const doubleAllowed = doubleAvailable(filters.size);

  // Changing Size / Sinks / Type swaps the displayed model, keeping the basin
  // type (U1 ramp / U2 vessel) and left/right orientation wherever an article
  // exists for the new combination.
  function changeFilters(next) {
    // Double only exists at 180cm - fall back to Single if the size can't take it.
    if (!doubleAvailable(next.size) && next.sinks === "double") next.sinks = "single";
    setFilters(next);
    const resolved = resolveCountertop(next, prefer);
    if (resolved && resolved.id !== countertop?.id) applyCountertop(resolved, false);
    else if (!resolved) { setCountertop(null); onCountertopModel(null); }
  }

  // Shared by the filters and the product grid.
  function applyCountertop(c, rememberPrefs = true) {
    setCountertop(c);
    onCountertopModel(c.model);
    if (rememberPrefs) setPrefer({ basin: c.basin, orientation: c.orientation ?? null });

    const stillValid =
      storage && storage !== "none" &&
      compatibleStorages(c).some((x) => x.id === storage.id);
    if (storage === "none") {
      if (!allowsNoStorage(c)) { setStorage(null); onStorageModel(null); }
    } else if (!stillValid) {
      setStorage(null); onStorageModel(null);
    }
  }

  // ── Selection handlers ────────────────────────────────
  function selectCountertop(c) {
    applyCountertop(c, true);
  }

  function selectStorage(s) {
    setStorage(s);
    onStorageModel(s.model);
  }

  function selectNoStorage() {
    setStorage("none");
    onStorageModel(null);
  }

  function selectCtColor(tex) {
    setCtColorId(tex.id);
    onTextureChange("countertop", tex.path);
    if (storageIsSolidSurface) onTextureChange("storage", tex.path);
  }

  function selectStColor(tex) {
    setStColorId(tex.id);
    onTextureChange("storage", tex.path);
  }

  // The default countertop is created in useState, which never notifies the
  // parent - push it up on mount so the canvas renders it straight away.
  useEffect(() => {
    if (countertop) onCountertopModel(countertop.model);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the pushed textures in sync. A solid-surface shelf mirrors the
  // countertop finish instead of using a laminate colour.
  useEffect(() => {
    if (!countertop) return;
    onTextureChange("countertop", ctColor?.path);
    onTextureChange("storage", storageIsSolidSurface ? ctColor?.path : stColor?.path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countertop, storage, ctColorId, stColorId, storageIsSolidSurface]);

  // Price + info sync.
  useEffect(() => {
    const storageObj = storage === "none" ? null : storage;
    onPriceChange(
      vanityTotal({ counter: countertop, storage: storageObj, countertopColorId: ctColorId })
    );
    onInfoChange(
      countertop
        ? { countertop, storage: storageObj, noStorage: storage === "none", ctColor, stColor }
        : null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countertop, storage, ctColorId, stColorId]);

  const toggle = (s) => () => setOpenSection((cur) => (cur === s ? null : s));

  return (
    <div>
      <h1 className="text-center text-lg tracking-[0.2em] font-semibold text-gray-800 py-5">
        CREATE YOUR OWN VANITY
      </h1>

      {/* Countertop Options */}
      <AccordionSection
        title="Countertop Options"
        isOpen={openSection === SECTIONS.COUNTERTOP}
        onToggle={toggle(SECTIONS.COUNTERTOP)}
      >
        <FilterBar
          filters={filters}
          onChange={changeFilters}
          sizes={SIZES}
          sinks={SINKS}
          thicknesses={TYPES}
          sizeImages={sizeImages}
          doubleAllowed={doubleAllowed}
        />
        <ProductGrid
          items={filteredCountertops}
          selectedId={countertop?.id}
          onSelect={selectCountertop}
          cols={3}
          emptyMessage="No countertops match these filters."
        />
      </AccordionSection>

      {/* Storage Options — only meaningful once a countertop is chosen */}
      <AccordionSection
        title="Storage Options"
        isOpen={openSection === SECTIONS.STORAGE}
        onToggle={toggle(SECTIONS.STORAGE)}
      >
        {countertop ? (
          <ProductGrid
            items={storageOptions}
            selectedId={storage && storage !== "none" ? storage.id : null}
            onSelect={selectStorage}
            cols={3}
            showNoStorage={canHaveNoStorage}
            noStorageSelected={storage === "none"}
            onSelectNoStorage={selectNoStorage}
            emptyMessage="No storage available for this countertop."
          />
        ) : (
          <p className="text-xs text-gray-400 px-5 py-6 text-center">
            Select a countertop first.
          </p>
        )}
      </AccordionSection>

      {/* Countertop Color */}
      <AccordionSection
        title="Countertop Color"
        isOpen={openSection === SECTIONS.CT_COLOR}
        onToggle={toggle(SECTIONS.CT_COLOR)}
      >
        <TextureGrid
          textures={countertopColors}
          selectedId={ctColorId}
          onSelect={selectCtColor}
          cols={5}
        />
      </AccordionSection>

      {/* Storage Color — hidden when No Storage */}
      {showStorageColor && (
        <AccordionSection
          title="Storage Color"
          isOpen={openSection === SECTIONS.ST_COLOR}
          onToggle={toggle(SECTIONS.ST_COLOR)}
        >
          <TextureGrid
            textures={storageColors}
            selectedId={stColorId}
            onSelect={selectStColor}
            cols={5}
          />
        </AccordionSection>
      )}
    </div>
  );
}
